/**
 * Configuração do Sentry para monitoramento de erros e performance
 */

import * as Sentry from '@sentry/react'
import { deduplicationBeforeSend } from './sentryErrorDeduplication'
import { detectAdBlockerCached } from '../utility/adBlockerDetection'
import { getApiUrl } from './apiConfig'

// DSN do Sentry - usa o DSN fornecido diretamente
const SENTRY_DSN = 'https://795d22942ea863d14e35974c9051793a@o4509538530951168.ingest.us.sentry.io/4510293420408832'

// Ambiente - staging ou production (fallback para development)
const SENTRY_ENV = import.meta.env.VITE_SENTRY_ENV || 'development'

// Flag para garantir que o Sentry só seja inicializado uma vez
let isInitialized = false

// Chunk de versão antiga que sumiu após deploy (Chrome / Firefox / Safari)
export const CHUNK_LOAD_ERROR = /dynamically imported module|Importing a module script failed/i


/**
 * Configura sample rates baseados no ambiente
 */
function getSampleRates() {
  const isProduction = SENTRY_ENV === 'production'
  const isStaging = SENTRY_ENV === 'staging'

  if (isProduction) {
    return {
      tracesSampleRate: 0.1, // 10% das transações em produção
      replaysSessionSampleRate: 0.1, // 10% das sessões em produção
      replaysOnErrorSampleRate: 1.0 // 100% das sessões com erro
    }
  }

  if (isStaging) {
    return {
      tracesSampleRate: 1.0, // 100% das transações em staging
      replaysSessionSampleRate: 1.0, // 100% das sessões em staging
      replaysOnErrorSampleRate: 1.0 // 100% das sessões com erro
    }
  }

  // Development
  return {
    tracesSampleRate: 1.0, // 100% em desenvolvimento
    replaysSessionSampleRate: 0.1, // 10% em desenvolvimento
    replaysOnErrorSampleRate: 1.0 // 100% das sessões com erro
  }
}

/**
 * Inicializa o Sentry
 * @param {Object} routerConfig - Configuração opcional do React Router para integração
 */
export async function initSentry(routerConfig = null) {
  // Evita inicialização múltipla
  if (isInitialized) {
    console.warn('[Sentry] Sentry já foi inicializado. Ignorando nova inicialização.')
    return Sentry
  }

  // Detecta bloqueador de anúncios antes de inicializar o Sentry
  // Se detectado, usa tunnel através do backend da aplicação
  // Também pode ser forçado via variável de ambiente VITE_SENTRY_FORCE_TUNNEL=true
  const forceTunnel = import.meta.env.VITE_SENTRY_FORCE_TUNNEL === 'true'
  let isBlocked = false
  
  if (!forceTunnel) {
    try {
      isBlocked = await detectAdBlockerCached()
    } catch (error) {
      console.error('[Sentry] Erro ao detectar bloqueador de anúncios:', error)
      // Em caso de erro, continua sem tunnel
      isBlocked = false
    }
  } else {
    isBlocked = true
    console.log('[Sentry] Tunnel forçado via variável de ambiente VITE_SENTRY_FORCE_TUNNEL')
  }

  const sampleRates = getSampleRates()

  // Prepara integrações
  const integrations = [
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true
    })
  ]

  // Adiciona integração do React Router v6 se configurado
  if (routerConfig) {
    integrations.push(
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: routerConfig.useEffect,
        useLocation: routerConfig.useLocation,
        useNavigationType: routerConfig.useNavigationType,
        createRoutesFromChildren: routerConfig.createRoutesFromChildren,
        matchRoutes: routerConfig.matchRoutes
      })
    )
  } else {
    // Fallback para browserTracingIntegration se React Router não estiver configurado
    integrations.push(Sentry.browserTracingIntegration())
  }

  // Configuração base do Sentry
  const sentryConfig = {
    dsn: SENTRY_DSN,
    environment: SENTRY_ENV,
    enabled: true,

    // Integrações
    integrations,

    // Sample rates baseados em ambiente
    ...sampleRates,

    // Tracing
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/.*\.theripen\.com/,
      /^https:\/\/.*\.test/,
      /^https:\/\/.*\.localhost/
    ],

    // Error monitoring
    beforeSend: (event, hint) => {
      // Um hash por deploy criaria uma issue nova a cada deploy; agrupa tudo em uma
      if (event.exception?.values?.some(v => CHUNK_LOAD_ERROR.test(v.value || ''))) {
        event.fingerprint = ['chunk-load-error']
      }
      return deduplicationBeforeSend(event, hint)
    },
    sendDefaultPii: true,
    attachStacktrace: true,

    // Logs
    enableLogs: true,

    // Debug mode apenas se explicitamente habilitado via variável de ambiente
    // Use VITE_SENTRY_DEBUG=true para habilitar logs detalhados
    debug: import.meta.env.VITE_SENTRY_DEBUG === 'true'
  }

  // Se detectado bloqueador ou forçado via env, usa tunnel através do backend
  // O endpoint do tunnel deve estar configurado no backend para encaminhar
  // os eventos do Sentry para o servidor oficial do Sentry
  if (isBlocked || forceTunnel) {
    // Usa a URL base da API configurada (mesma que a API usa)
    // O endpoint será: {API_BASE_URL}/sentry/tunnel
    // O backend precisa ter esse endpoint configurado para fazer proxy dos eventos
    const tunnelUrl = getApiUrl('/sentry/tunnel')
    sentryConfig.tunnel = tunnelUrl
    console.warn('[Sentry] Usando tunnel através do backend em', tunnelUrl, 
      forceTunnel ? '(forçado via env)' : '(bloqueador detectado)')
  } else {
    console.log('[Sentry] Nenhum bloqueador detectado. Usando endpoint direto do Sentry.')
  }

  // Inicializa o Sentry
  Sentry.init(sentryConfig)

  // Define release se disponível
  if (import.meta.env.VITE_APP_VERSION) {
    Sentry.setRelease(import.meta.env.VITE_APP_VERSION)
  }

  // Marca como inicializado
  isInitialized = true

  return Sentry
}

// Exporta o Sentry para uso em outros lugares
export default Sentry

